import React, { useMemo } from "react";
import { Breadcrumb, Layout, Tabs, Tag, theme } from "antd";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useMarketerRoot, useClassicRoot, useApiAccessEnabled } from "../../hooks/useDashboardConfig";
import {
    ApiOutlined,
    PartitionOutlined,
    SettingOutlined,
    UserOutlined,
} from "@ant-design/icons";

const { Content } = Layout;

type TabKey = "list" | "fields" | "segments" | "integrations";

// Tab paths are computed from a base so this layout works both at
// /dashboard/customers and embedded in the Marketer View shell
// (/marketer-view/:brandHash/admin/customers) without breaking out.
const buildTabPaths = (base: string): Record<TabKey, string> => ({
    list: base,
    fields: `${base}/fields`,
    segments: `${base}/segments`,
    integrations: `${base}/integrations`,
});

const resolveActiveKey = (pathname: string, tabPaths: Record<TabKey, string>): TabKey => {
    if (pathname.startsWith(tabPaths.fields)) return "fields";
    if (pathname.startsWith(tabPaths.segments)) return "segments";
    if (pathname.startsWith(tabPaths.integrations)) return "integrations";
    return "list";
};

export const CustomersLayout: React.FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const { brandHash } = useParams();
    const marketerRoot = useMarketerRoot();
    const classicRoot = useClassicRoot();
    // Integrations rides on the public API (webhooks, keys, docs) — until
    // that's live (API_ACCESS_ENABLED) the tab is a disabled "Coming soon".
    const apiAccessEnabled = useApiAccessEnabled();
    const tabPaths = useMemo(
        () =>
            buildTabPaths(
                brandHash
                    ? `${marketerRoot}/${brandHash}/admin/customers`
                    : `${classicRoot}/customers`,
            ),
        [brandHash, marketerRoot, classicRoot],
    );
    const activeKey = useMemo<TabKey>(
        () => resolveActiveKey(location.pathname, tabPaths),
        [location.pathname, tabPaths],
    );

    const items = [
        {
            key: "list" as TabKey,
            label: (
                <span>
                    <UserOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: "customers.tabs.list", defaultMessage: "Customers" })}
                </span>
            ),
        },
        {
            key: "segments" as TabKey,
            label: (
                <span>
                    <PartitionOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: "customers.tabs.segments", defaultMessage: "Saved Segments" })}
                </span>
            ),
        },
        {
            key: "fields" as TabKey,
            label: (
                <span>
                    <SettingOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: "customers.tabs.fields", defaultMessage: "Fields" })}
                </span>
            ),
        },
        {
            key: "integrations" as TabKey,
            disabled: !apiAccessEnabled,
            label: (
                <span>
                    <ApiOutlined style={{ marginRight: 8 }} />
                    {intl.formatMessage({ id: "customers.tabs.integrations", defaultMessage: "Integrations" })}
                    {!apiAccessEnabled && (
                        <Tag style={{ marginLeft: 8 }}>
                            {intl.formatMessage({ id: "customers.tabs.comingSoon", defaultMessage: "Coming soon" })}
                        </Tag>
                    )}
                </span>
            ),
        },
    ];

    return (
        <Content style={{ padding: 24 }}>
            <Breadcrumb
                style={{ marginBottom: 16 }}
                items={[
                    { title: intl.formatMessage({ id: "breadcrumb.dashboard", defaultMessage: "Dashboard" }) },
                    { title: intl.formatMessage({ id: "customers.breadcrumb", defaultMessage: "Customers" }) },
                ]}
            />
            <div
                style={{
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                    padding: "8px 16px 0",
                    marginBottom: 16,
                }}
            >
                <Tabs
                    activeKey={activeKey}
                    items={items}
                    onChange={(key) => navigate(tabPaths[key as TabKey])}
                />
            </div>
            <Outlet />
        </Content>
    );
};

export default CustomersLayout;
